import "moment/min/locales";

class CommonUtil {
  static renameProperty(object, oldProperty, newProperty) {
    if (object.hasOwnProperty(oldProperty)) {
      Object.defineProperty(
        object,
        newProperty,
        Object.getOwnPropertyDescriptor(object, oldProperty)
      );
      delete object[oldProperty];
    }
    return object;
  }

  static concatStringPropertiesWithSpace(object, propertyNames) {
    var properties = [];

    propertyNames.forEach(propertyName => {
      properties.push(object[propertyName]);
    });

    return this.concatStringsWithDelimiter(" ", ...properties);
  }

  static constructStringArrayWithoutEmpties(...strings) {
    var array = [];

    for (var i = 0; i < strings.length; i++) {
      if (strings[i].length !== 0) {
        array.push(strings[i]);
      }
    }

    return array;
  }

  static constructStringArrayFromObjectArray(objectArray, propertyName) {
    var array = [];
    if (objectArray != null) {
      for (var i = 0; i < objectArray.length; i++) {
        if (objectArray[i] !== null && objectArray[i][propertyName] != null) {
          array.push(objectArray[i][propertyName]);
        }
      }
    }

    return array;
  }

  static constructObjectArrayFromStringArray(stringArray, propertyName) {
    var objectArray = [];

    if (stringArray !== null) {
      for (var i = 0; i < stringArray.length; i++) {
        if (stringArray[i] !== null) {
          var object = {};
          object[propertyName] = stringArray[i].toUpperCase();
          objectArray.push(object);
        }
      }
    }

    return objectArray;
  }

  static getFullName(object) {
    var propertyNames = [];

    if (object.hasOwnProperty("firstName")) {
      propertyNames.push("firstName");
    } else {
      propertyNames.push("name");
    }
    propertyNames.push("lastName");

    return this.concatStringPropertiesWithSpace(object, propertyNames);
  }

  static _checkStringsEmptiness(response, strings) {
    for (var i = 0; i < strings.length; i++) {
      if (strings[i].length === 0) {
        return !response;
      }
    }

    return response;
  }

  static isAnyStringEmpty(strings) {
    return this._checkStringsEmptiness(false, strings);
  }

  static isEveryStringNotEmpty(strings) {
    return this._checkStringsEmptiness(true, strings);
  }

  static _stringMatchRegex(charRegex, string) {
    return charRegex.test(string);
  }

  static stringHasDigit(string) {
    return CommonUtil._stringMatchRegex(/\d/, string);
  }

  static stringHasUpperCasedChar(string) {
    return CommonUtil._stringMatchRegex(/[A-Z]/, string);
  }

  static stringHasLowerCasedChar(string) {
    return CommonUtil._stringMatchRegex(/[a-z]/, string);
  }

  static stringHasSpecialChar(string) {
    return CommonUtil._stringMatchRegex(/[^A-Za-z0-9]/, string);
  }

  static arrayHasObjectWithProperty(array, propertyName, propertyValue) {
    var objectFound = false;

    for (var i = 0; i < array.length; i++) {
      if (
        array[i][propertyName] !== undefined &&
        array[i][propertyName] === propertyValue
      ) {
        objectFound = true;
        break;
      }
    }

    return objectFound;
  }

  static getFirstArrayObjectWithProperty(array, propertyName, propertyValue) {
    for (var i = 0; i < array.length; i++) {
      if (
        array[i][propertyName] !== undefined &&
        array[i][propertyName] === propertyValue
      ) {
        return array[i];
      }
    }
  }

  static formatDate(string) {
    var date = new Date(string);
    return date.toLocaleDateString();
  }

  static formatSystemDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = "" + d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    if (year.length < 4) {
      while (year.length < 4) {
        year = "0" + year;
      }
    }
    return [year, month, day].join("-");
  }

  static queryParamsGetValueForKey(key, url) {
    key = key.replace(/[[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + key + "(=([^&#]*)|&|#|$)"),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  static sortArrayByDateProperty(array, propertyName, direction = "desc") {
    if (array) {
      const sorter = direction === "desc" ? 1 : direction === "asc" ? -1 : 0;

      const sorted = array.sort((o1, o2) => {
        const date1 = new Date(this.getDeep(o1, propertyName));
        const date2 = new Date(this.getDeep(o2, propertyName));

        return date1 < date2 ? sorter : -1 * sorter;
      });

      return sorted;
    }
  }

  static ifNotExternalLinkCreateHttp(text) {
    if (!text) return undefined;
    var regex = "^((http|https|ftp)://)";

    if (!text.match(regex)) {
      return "http://" + text;
    } else {
      return text;
    }
  }

  static removeCharFromBeginingOfString(string, character) {
    while (string.charAt(0) === character) {
      string = string.substr(1);
    }
    return string;
  }

  /**
   * Safely gets deep value.
   */
  static getDeep = (object, path = "") => {
    path = path.replace(/\//g, ".");
    path = CommonUtil.removeCharFromBeginingOfString(path, ".");

    return path
      .split(".")
      .reduce((o, x) => (o === undefined ? o : o[x]), object);
  };

  /**
   * Dynamically sets a deeply nested value in an object.
   * Optionally "bores" a path to it if its undefined.
   * @function
   * @param {!object} obj  - The object which contains the value you want to change/set.
   * @param {!array} path  - The array representation of path to the value you want to change/set. Use split if you have string value.
   * @param {!mixed} value - The value you want to set it to.
   * @param {boolean} setrecursively - If true, will set value of non-existing path as well.
   */
  static setDeep(obj, path, value, setrecursively = false) {
    if (!path) {
      return;
    }

    let level = 0;

    path.reduce((a, b) => {
      level++;

      if (
        setrecursively &&
        typeof a[b] === "undefined" &&
        level !== path.length
      ) {
        a[b] = {};
        return a[b];
      }

      if (level === path.length) {
        a[b] = value;
        return value;
      } else {
        return a[b];
      }
    }, obj);
  }

  /**
   * Safely deletes deep value.
   *
   * If newValue is anything else than undefined, it will be replaced
   */
  static deleteDeep = (object, path = "") => {
    if (!path || !object) {
      return;
    }

    path = path.replace(/\//g, ".");

    const parts = path.split(".");

    CommonUtil._recursiveDeleteDeep(object, parts);
  };

  /**
   * If newValue is anything else than undefined, it will be replaced
   */
  static _recursiveDeleteDeep = (object, pathParts, newValue) => {
    if (!object || !pathParts || pathParts.length === 0) {
      return;
    }

    const part = pathParts[0];
    CommonUtil.removeFromArrayAtBegining(pathParts);

    if (part === undefined) {
      return;
    }
    if (part === "") {
      this._recursiveDeleteDeep(object, pathParts, newValue);
      return;
    }

    const partRegex = new RegExp(part);
    Object.keys(object)
      .filter(key => {
        const match = key.match(partRegex);
        return match && key === match[0];
      })
      .forEach(key => {
        if (pathParts.length === 0) {
          if (newValue === undefined) {
            delete object[key];
          } else {
            object[key] = newValue;
          }
        } else {
          this._recursiveDeleteDeep(object[key], [...pathParts], newValue);
        }
      });
  };

  static hasDeepValue = (object, path = "", value) => {
    const deep = CommonUtil.getDeep(object, path);
    return deep === value;
  };

  static setDeepOnStringPath(obj, path, value, setrecursively = false) {
    path = path.replace(/\//g, ".");
    path = CommonUtil.removeCharFromBeginingOfString(path, ".");

    return this.setDeep(obj, path.split("."), value, setrecursively);
  }

  static getNestedUrlWithIds(paths, ids, idsKnown = false) {
    if (
      !paths ||
      !ids ||
      paths.length < ids.length ||
      paths.length > ids.length + 1
    ) {
      throw new Error("IllegalArguments");
    }

    let url = "";
    for (let i = 0; i < paths.length; i++) {
      url += paths[i];
      if (i < ids.length) {
        url += "/";
        if (!idsKnown) {
          url += ":";
        }
        url += ids[i];
      }
    }
    return url;
  }

  static lastIndexOfObjectWithPoperty(array, propertyName, propertyValue) {
    var index = -1;

    for (var i = 0; i < array.length; i++) {
      if (this.getDeep(array[i], propertyName) === propertyValue) {
        index = i;
      }
    }

    return index;
  }

  static arrayContainsObjectWithProperty(array, propertyName, propertyValue) {
    return (
      this.lastIndexOfObjectWithPoperty(array, propertyName, propertyValue) > -1
    );
  }

  static removeElementFromArray(array, element) {
    if (array) {
      const index = array.indexOf(element);
      CommonUtil.removeFromArrayAtIndex(array, index);
    }
  }

  static findItemInTreeById(tree, id) {
    let item = null;
    if (tree && tree.length > 0) {
      tree.forEach(element => {
        if (item === null) {
          if (element.id === id) {
            item = element;
          } else if (element.children && element.children.length > 0) {
            item = this.findItemInTreeById(element.children, id);
          }
        }
      });
    }
    return item;
  }

  static addSubTreeToTree(tree, subTree) {
    if (tree && subTree) {
      subTree.forEach(subNode => {
        const exists = CommonUtil.arrayContainsObjectWithProperty(
          tree,
          "id",
          subNode.id
        );
        if (exists) {
          const node = CommonUtil.getFirstArrayObjectWithProperty(
            tree,
            "id",
            subNode.id
          );
          this.addSubTreeToTree(node.children, subNode.children);
        } else {
          tree.push(...subTree);
        }
      });
    }
  }

  static getAllTreeNodeProperties(
    tree,
    propertyName,
    childrenPropertyName = "children"
  ) {
    var properties = [];

    if (tree) {
      tree.forEach(n => {
        properties.push(n[propertyName]);

        const childrenProperties = CommonUtil.getAllTreeNodeProperties(
          n[childrenPropertyName],
          propertyName,
          childrenPropertyName
        );

        properties = CommonUtil.joinArrays(properties, childrenProperties);
      });
    }

    return properties;
  }

  static treeHasNodes(
    tree,
    nodes,
    uniquePropertyName,
    childrenPropertyName = "children"
  ) {
    if (tree && nodes) {
      const matches = this.searchTreeForNodesMatches(
        tree,
        nodes,
        uniquePropertyName,
        childrenPropertyName
      );
      return matches >= nodes.length;
    }
    return false;
  }

  static searchTreeForNodesMatches(
    tree,
    nodes,
    uniquePropertyName,
    childrenPropertyName = "children"
  ) {
    var matches = 0;

    if (tree) {
      tree.forEach(n => {
        if (n) {
          if (
            (!uniquePropertyName && nodes.includes(n)) ||
            (uniquePropertyName &&
              CommonUtil.arrayContainsObjectWithProperty(
                nodes,
                uniquePropertyName,
                CommonUtil.getDeep(n, uniquePropertyName)
              ))
          ) {
            matches++;
          }

          matches += CommonUtil.searchTreeForNodesMatches(
            n[childrenPropertyName],
            nodes,
            uniquePropertyName,
            childrenPropertyName
          );
        }
      });
    }

    return matches;
  }

  static getTreeNodeParent(
    tree,
    node,
    uniquePropertyName,
    childrenPropertyName = "children",
    parent
  ) {
    var nodeParent = undefined;

    if (tree) {
      tree.forEach(n => {
        if (n && !nodeParent) {
          if (
            (!uniquePropertyName && node === n) ||
            (uniquePropertyName &&
              n[uniquePropertyName] === node[uniquePropertyName])
          ) {
            nodeParent = parent;
          } else {
            nodeParent = CommonUtil.getTreeNodeParent(
              n[childrenPropertyName],
              node,
              uniquePropertyName,
              childrenPropertyName,
              n
            );
          }
        }
      });
    }

    return nodeParent;
  }

  static setTreeNodeParent(
    tree,
    node,
    parent,
    uniquePropertyName,
    childrenPropertyName = "children"
  ) {
    const currentParent = this.getTreeNodeParent(
      tree,
      node,
      uniquePropertyName,
      childrenPropertyName
    );

    if (currentParent) {
      const childIndex = CommonUtil.lastIndexOfObjectWithPoperty(
        currentParent[childrenPropertyName],
        uniquePropertyName,
        node[uniquePropertyName]
      );
      currentParent[childrenPropertyName][childIndex] = parent;
    } else {
      tree.push(parent);
    }

    parent[childrenPropertyName] = parent[childrenPropertyName]
      ? parent[childrenPropertyName]
      : [];
    parent[childrenPropertyName].push(node);
  }

  static sortTreeByLevels(
    tree,
    sortFunction,
    childrenPropertyName = "children"
  ) {
    if (tree && sortFunction) {
      tree.sort(sortFunction);

      tree.forEach(n =>
        CommonUtil.sortTreeByLevels(
          n[childrenPropertyName],
          sortFunction,
          childrenPropertyName
        )
      );
    }
  }

  static dateEquals(date1, date2) {
    if (!date1 && !date2) return true;

    const time1 = new Date(date1).getTime();
    const time2 = new Date(date2).getTime();

    return time1 === time2;
  }

  static arraysEquals(
    arr1,
    arr2,
    name = undefined,
    additionalChecks = undefined
  ) {
    // Check if the arrays are the same length
    if (arr1.length !== arr2.length) return false;

    // Check if all items exist and are in the same order
    for (var i = 0; i < arr1.length; i++) {
      if (!this.genericEquals(arr1[i], arr2[i], name, additionalChecks))
        return false;
    }

    // Otherwise, return true
    return true;
  }

  /**
   * If params are objects they are compared with
   * id property. Only exception is dateFromTo object type.
   * @param {*} obj1
   * @param {*} obj2
   */
  static genericEquals(
    obj1,
    obj2,
    name = undefined,
    additionalChecks = undefined
  ) {
    if (obj1 && obj2 && typeof obj1 === "object") {
      if (Array.isArray(obj1)) {
        return this.arraysEquals(obj1, obj2, name, additionalChecks);
      } else if (
        obj1.hasOwnProperty("dateFrom") &&
        obj1.hasOwnProperty("dateTo")
      ) {
        return (
          this.dateEquals(obj1.dateFrom, obj2.dateFrom) &&
          this.dateEquals(obj1.dateTo, obj2.dateTo)
        );
      } else {
        const idsEqual = obj1.id === obj2.id;

        const additionalEqual = this.additionalEquals(
          obj1,
          obj2,
          name,
          additionalChecks
        );
        return idsEqual && additionalEqual;
      }
    } else {
      return obj1 === obj2;
    }
  }

  static additionalEquals(
    obj1,
    obj2,
    name = undefined,
    additionalChecks = undefined
  ) {
    var additionalEqual = true;

    additionalChecks = CommonUtil.calculateAdditionalChecks(
      additionalChecks,
      name
    );
    if (additionalChecks && name) {
      for (let i = 0; i < additionalChecks.length; i++) {
        const obj1Additional = CommonUtil.getDeep(obj1, additionalChecks[i]);
        const obj2Additional = CommonUtil.getDeep(obj2, additionalChecks[i]);

        if (obj1Additional !== obj2Additional) {
          additionalEqual = false;
        }
      }
    }

    return additionalEqual;
  }

  static calculateAdditionalChecks = (additionalChecks, name) => {
    if (additionalChecks && name) {
      additionalChecks = CommonUtil.jsonCopy(additionalChecks);
      for (let i = 0; i < additionalChecks.length; i++) {
        if (
          additionalChecks[i].startsWith(name) &&
          additionalChecks[i].length > name.length &&
          additionalChecks[i].substring(name.length).startsWith(".")
        ) {
          additionalChecks[i] = additionalChecks[i].substring(name.length + 1);
        }
      }
    }

    return additionalChecks;
  };

  static genericEmpty(obj) {
    if (!obj) return true;
    if (typeof obj === "object") {
      if (Array.isArray(obj)) {
        return obj.length ? false : true;
      } else {
        return Object.keys(obj)
          .map(key => obj[key])
          .every(value => this.genericEmpty(value));
      }
    }

    return obj === "";
  }

  static checkValuesChanged(oldEntity, entity, additionalChecks = undefined) {
    if (oldEntity && entity) {
      return Object.keys(oldEntity)
        .map(key => ({
          oldValue: oldEntity[key],
          value: entity[key],
          propertyName: key
        }))
        .some(
          property =>
            !this.genericEquals(
              property.oldValue,
              property.value,
              property.propertyName,
              additionalChecks
            )
        );
    } else if (entity) {
      return !this.genericEmpty(entity);
    } else {
      return false;
    }
  }

  static getLastObjectFromArrayWithNestedProperty(
    array,
    propertyName,
    propertyValue
  ) {
    var object = undefined;

    const index = this.lastIndexOfObjectWithPoperty(
      array,
      propertyName,
      propertyValue
    );

    if (index > -1) {
      object = array[index];
    }

    return object;
  }

  static objectHasAnyProperty(object) {
    return object !== undefined && Object.keys(object).length > 0;
  }

  static objectHasAnyDeepProperty(object, subPropertyPath) {
    var hasAnyDeep = false;

    if (object !== undefined) {
      Object.keys(object).forEach(key => {
        const subProperty = this.getDeep(object[key], subPropertyPath);
        if (subProperty) {
          if (Array.isArray(subProperty)) {
            if (subProperty.length) {
              hasAnyDeep = true;
            }
          } else {
            hasAnyDeep = true;
          }
        }
      });
    }

    return hasAnyDeep;
  }

  static objectHasProperty(object, propertyName, canBeUndefined = false) {
    var hasProperty = object && object.hasOwnProperty(propertyName);

    if (!canBeUndefined && hasProperty) {
      hasProperty =
        object[propertyName] !== undefined && object[propertyName] !== null;
    }

    return hasProperty;
  }

  static objectHasOnlyOneProperty(
    object,
    propertyName,
    canBeUndefined = false
  ) {
    const hasProperty = CommonUtil.objectHasProperty(
      object,
      propertyName,
      canBeUndefined
    );
    const hasOnlyOne = object !== undefined && Object.keys(object).length === 1;

    return hasOnlyOne && hasProperty;
  }

  static objectsHasSameProperty(object1, object2, propertyName) {
    const property1 = this.getDeep(object1, propertyName);
    const property2 = this.getDeep(object2, propertyName);

    return property1 === property2;
  }

  static constructHeaderTitleAndId(title, id) {
    return id ? `${title} (${id})` : title;
  }

  static concatArrays(...arrays) {
    var result = [];

    for (var i = 0; i < arrays.length; i++) {
      if (arrays[i] !== undefined) {
        result = result.concat(arrays[i]);
      }
    }

    return result;
  }

  static getDataContent(data) {
    return data?._embedded?.content?.length > 0 && data._embedded.content;
  }

  static accumulateArrayProperties(array, propertyName) {
    var accumulated = {};

    if (array) {
      for (var i = 0; i < array.length; i++) {
        if (array[i][propertyName]) {
          const newPropName = Object.keys(array[i][propertyName])[0];
          accumulated[newPropName] = array[i][propertyName][newPropName];
        }
      }
    }

    return accumulated;
  }

  static replaceStringAfterLastSlash(string, replacement) {
    return string.replace(/.$/, replacement);
  }

  static replaceStringAfterLastOccurrence(string, substring, replacement) {
    const replaceFrom = string.lastIndexOf(substring) + 1;
    return string.substring(0, replaceFrom) + replacement;
  }

  static groupArrayByProperty(array, propertyName) {
    return array.reduce((rv, x) => {
      (rv[x[propertyName]] = rv[x[propertyName]] || []).push(x);
      return rv;
    }, {});
  }

  static groupArrayToTreeByProperty(
    array,
    propertyName,
    treeableDelimiter,
    nodeValuePropertyName,
    onlyNodesHaveValue = true
  ) {
    var result = {};

    array.forEach(el =>
      el[propertyName]
        .split(treeableDelimiter)
        .filter(x => x !== "")
        .reduce((o, k) => {
          var newEl = {};

          if (!onlyNodesHaveValue || el[propertyName].endsWith(k)) {
            newEl[nodeValuePropertyName] = el;
          }

          if (o[k]) {
            newEl = { ...newEl, ...o[k] };
          }
          return (o[k] = newEl);
        }, result)
    );

    return result;
  }

  static ungroupTreeToArrayByProperty(tree, propertyName, condition) {
    var array = [];

    if (CommonUtil.objectHasAnyProperty(tree)) {
      Object.keys(tree).forEach(nodeName => {
        const subArray = CommonUtil.ungroupTreeToArrayByProperty(
          CommonUtil.objectWithoutProperties(tree[nodeName], [propertyName]),
          propertyName,
          condition
        );
        array = array.concat(subArray);

        if (tree[nodeName][propertyName]) {
          array.push(tree[nodeName][propertyName]);
        }
      });
      if (tree[propertyName]) {
        array.push(tree[propertyName]);
      }
    }

    return array;
  }

  static processTree = (root, processFunc) => {
    for (var key in root) {
      processFunc(root, key);
      if (root[key] !== null && typeof root[key] == "object") {
        //going one step down in the object tree!!
        CommonUtil.processTree(root[key], processFunc);
      }
    }
  };

  static filterTreeNodeSiblingNames = (parent, currentNodeName, filterFunc) => {
    return Object.keys(parent)
      .filter(nodeName => nodeName !== currentNodeName)
      .filter(nodeName => filterFunc(parent[nodeName]));
  };

  static filterTreeNodeSiblingNamesWithDeepValue = (
    parent,
    currentNodeName,
    path,
    value
  ) => {
    return CommonUtil.filterTreeNodeSiblingNames(
      parent,
      currentNodeName,
      node => CommonUtil.hasDeepValue(node, path, value)
    );
  };

  static replaceAllObjectSubSubPropertiesValues(
    object,
    subSubPropertyName,
    value
  ) {
    Object.keys(object).forEach(key => {
      CommonUtil.setDeepOnStringPath(
        object,
        `${key}.${subSubPropertyName}`,
        value
      );
    });
  }

  static trimLongWord(word, maxNumberOfCharacters, trailingString) {
    const trimmedWord =
      word && word.length > maxNumberOfCharacters
        ? word.substring(0, maxNumberOfCharacters) + trailingString
        : word;
    return trimmedWord;
  }

  static addToStringWithBrackets(string, additions, showUndefined = false) {
    if (CommonUtil.objectHasAnyProperty(additions)) {
      string = `${string} (${CommonUtil.objectToString(
        additions,
        ", ",
        showUndefined
      )})`;
    }

    return string;
  }

  static objectToString(object, propertyDelimiter, showUndefined = false) {
    var string = "";
    var i = 0;

    if (object) {
      Object.keys(object)
        .filter(key => (showUndefined ? true : object[key]) !== undefined)
        .forEach(key => {
          const delimiter = i++ === 0 ? "" : propertyDelimiter;
          string = `${string}${delimiter}${key}:${object[key]}`;
        });
    }

    return string;
  }

  static objectWithoutProperties(obj, keys) {
    var target = {};
    for (var i in obj) {
      if (keys.indexOf(i) >= 0) continue;
      if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
      target[i] = obj[i];
    }
    return target;
  }

  static deleteObjectProperties(object, keys, leaveKeys = true) {
    Object.keys(object)
      .filter(
        key =>
          (!keys.includes(key) && leaveKeys) ||
          (keys.includes(key) && !leaveKeys)
      )
      .forEach(key => {
        delete object[key];
      });
  }

  /**
   * If newValue is anything else than undefined, it will be replaced
   */
  static deleteDeepObjectProperties(object, keys) {
    if (object && keys) {
      for (let i = 0; i < keys.length; i++) {
        CommonUtil.deleteDeep(object, keys[i]);
      }
    }
  }

  static removeAllFromArrayAfterIndex(array, deletionIndex) {
    if (array && deletionIndex > -1) {
      array.splice(deletionIndex);
    }
  }

  static removeSomeFromArrayAfterIndex(
    array,
    deletionIndex,
    numberOfDeletions
  ) {
    if (array && deletionIndex > -1) {
      array.splice(deletionIndex, numberOfDeletions);
    }
  }

  static removeFromArrayAtIndex(array, deletionIndex) {
    CommonUtil.removeSomeFromArrayAfterIndex(array, deletionIndex, 1);
  }

  static removeFromArrayAtBegining(array) {
    CommonUtil.removeFromArrayAtIndex(array, 0, 1);
  }

  static insertToArrayAtIndex(array, insertionIndex, newElement) {
    if (array && insertionIndex > -1) {
      array.splice(insertionIndex, 0, newElement);
    }
  }

  static insertToArrayBeginning(array, newElement) {
    CommonUtil.insertToArrayAtIndex(array, 0, newElement);
  }

  static stringToBoolean(booleanString) {
    return typeof booleanString === "string"
      ? /true/i.test(booleanString)
      : booleanString;
  }

  static concatStrings(...strings) {
    return this.concatStringsWithDelimiter("", ...strings);
  }
  static concatStringsWithDelimiter(delimiter, ...strings) {
    var concated = "";

    if (strings) {
      for (var i = 0; i < strings.length; i++) {
        const stringDelimiter = concated !== "" ? delimiter : "";
        concated = `${concated}${stringDelimiter}${strings[i]}`;
      }
    }

    return concated;
  }

  /**
   * Returns true only if JSON representation of both objects
   * are string-equals, i.e. if all their (sub)properties are the
   * same and the order of all (sub)properties is the same.
   */
  static jsonEquals(object1, object2) {
    return object1 === undefined || object2 === undefined
      ? object1 === object2
      : JSON.stringify(object1) === JSON.stringify(object2);
  }

  /**
   * Replacing only first-level properties
   */
  static replaceObjectProperties(
    object,
    replacement,
    irreplaceableProperties = []
  ) {
    Object.keys(replacement).forEach(propName => {
      if (
        irreplaceableProperties === undefined ||
        (irreplaceableProperties && !irreplaceableProperties.includes(propName))
      ) {
        object[propName] = replacement[propName];
      }
    });
  }

  static getFirstElementsFromArray(array, number) {
    // return array.slice(number);
    return this.getElementsFromArray(array, 0, number);
  }
  static getLastElementsFromArray(array, number) {
    // return array.slice(number * -1);
    return this.getElementsFromArray(array, 0, number * -1);
  }
  static getElementsFromArray(array, fromIndex, toIndex) {
    return array.slice(fromIndex, toIndex);
  }

  static joinArrays(
    array1,
    array2,
    array1From = 0,
    array1To = array1.length,
    array2From = 0,
    array2To = array2.length
  ) {
    return this.getElementsFromArray(array1, array1From, array1To).concat(
      this.getElementsFromArray(array2, array2From, array2To)
    );
  }

  static jsonCopy = object => {
    return object !== undefined
      ? JSON.parse(JSON.stringify(object))
      : undefined;
  };

  static replaceStringRegexPart(string, regex, replacement) {
    if (typeof regex === "string") {
      regex = new RegExp(regex);
    }

    if (regex instanceof RegExp) {
      return string.replace(regex, replacement);
    }
  }

  static getAllObjectPropertiesPaths(
    object,
    pathDelimiter,
    startWithDelimiter = false
  ) {
    let paths = [];
    let nodes = [
      {
        val: object,
        path: startWithDelimiter ? pathDelimiter : ""
      }
    ];
    while (nodes.length > 0) {
      let n = nodes.pop();
      paths.push(n);
      if (n.val) {
        Object.keys(n.val).forEach(k => {
          let path =
            n.path === pathDelimiter
              ? `${n.path}${k}`
              : `${n.path}${pathDelimiter}${k}`;

          if (typeof n.val[k] === "object") {
            nodes.unshift({
              val: n.val[k],
              path: path
            });
          } else {
            paths.push({
              val: n.val[k],
              path: path
            });
          }
        });
      }
    }
    return paths;
  }

  static substringAfterLastOccurrence(string, character) {
    return string.substring(string.lastIndexOf(character) + 1);
  }

  static countEquals(value, ...params) {
    var equals = 0;

    for (let i = 0; i < params.length; i++) {
      if (params[i] === value) {
        equals++;
      }
    }

    return equals;
  }

  static urlEndsWithNumber(url) {
    const pathTree = url.split("/");
    const lastPath = pathTree[pathTree.length - 1];
    return !isNaN(lastPath);
  }

  static getSafeStringLength(someString) {
    return someString ? someString.length : 0;
  }
}

export function is(x) {
  return !(x === undefined || x === null || x === false);
}

export function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

export default CommonUtil;
